import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { connect, disconnect } from '../api/modules/realtimeMessaging';
import { sendAnalytics } from '../telemetry/client';
import { getPersistentPlayerId } from '../api';
import config from '../config.json';

// List of paths that require WebSocket connection
const WEBSOCKET_PATHS = ['/j/', '/jd/', '/jt/', '/p/', '/pt/', '/pd/', '/q/'];

// Quickstart paths where fast-start owns the connection lifecycle
const QUICKSTART_PATHS = ['/q/'];

export const useWebSocket = () => {
  const location = useLocation();
  const isConnected = useRef(false);
  const connectionTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const shouldConnect = WEBSOCKET_PATHS.some((path) => location.pathname.startsWith(path));
    const isQuickstart = QUICKSTART_PATHS.some((path) => location.pathname.startsWith(path));

    // When fast-start is enabled, the QuickstartCoordinator owns the WS connection for quickstart paths
    if (config.USE_FAST_START && isQuickstart) {
      return;
    }

    if (shouldConnect && !isConnected.current) {
      if (connectionTimeout.current) {
        clearTimeout(connectionTimeout.current);
        connectionTimeout.current = null;
      }

      connectionTimeout.current = setTimeout(() => {
        console.log('[WebSocket] Connecting immediately for path:', location.pathname);

        if (isQuickstart) {
          const quickstartSlug = location.pathname.split('/q/')[1]?.split('/')[0] || undefined;
          sendAnalytics({
            name: 'WebSocket Connection Started',
            role: 'player',
            flow: 'quickstart',
            quickstartSlug,
            persistentId: getPersistentPlayerId(),
            isFastStart: false,
          });
        }

        connect();
        isConnected.current = true;
      }, 10);
    } else if (!shouldConnect && isConnected.current) {
      console.log('[WebSocket] Disconnecting for path:', location.pathname);
      disconnect();
      isConnected.current = false;
    }

    return () => {
      if (connectionTimeout.current) {
        clearTimeout(connectionTimeout.current);
        connectionTimeout.current = null;
      }
      if (isConnected.current) {
        console.log('[WebSocket] Cleaning up connection');
        disconnect();
        isConnected.current = false;
      }
    };
  }, [location.pathname]);
};
